import Link from 'next/link'
import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { FaFileContract, FaShieldAlt, FaCookieBite, FaUserTie, FaUsers, FaChevronRight, FaExternalLinkAlt, FaCreditCard, FaGavel } from 'react-icons/fa'
import { BsArrowRight } from 'react-icons/bs'

const TableOfContents = () => {
  const sections = [
    { id: 'general-terms', label: 'General Terms', icon: FaFileContract },
    { id: 'cookie-terms', label: 'Cookie Usage', icon: FaCookieBite },
    { id: 'terms-for-consumer-clients', label: 'Consumer Client Terms', icon: FaUsers },
    { id: 'terms-for-service-providers', label: 'Service Provider Terms', icon: FaUserTie },
  ]

  return (
    <div className="bg-slate-50 rounded-2xl p-6 mb-12">
      <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
        <FaFileContract className="text-orange-500" />
        Table of Contents
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {sections.map((section, index) => (
          <a
            key={index}
            href={`#${section.id}`}
            className="flex items-center gap-3 p-3 bg-white rounded-xl hover:bg-orange-50 hover:border-orange-200 border border-slate-200 transition-all group"
          >
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-500 transition-colors">
              <section.icon className="text-orange-600 group-hover:text-white transition-colors" />
            </div>
            <span className="text-slate-700 group-hover:text-orange-600 font-medium transition-colors">{section.label}</span>
            <FaChevronRight className="ml-auto text-slate-400 group-hover:text-orange-500 transition-colors" />
          </a>
        ))}
        <a
          href="/terms/payments-security"
          target="_blank"
          rel="noopener"
          className="flex items-center gap-3 p-3 bg-white rounded-xl hover:bg-orange-50 hover:border-orange-200 border border-slate-200 transition-all group"
        >
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-500 transition-colors">
            <FaCreditCard className="text-orange-600 group-hover:text-white transition-colors" />
          </div>
          <span className="text-slate-700 group-hover:text-orange-600 font-medium transition-colors">Payment Security</span>
          <FaExternalLinkAlt className="ml-auto text-slate-400 group-hover:text-orange-500 text-xs transition-colors" />
        </a>
      </div>
    </div>
  )
}

const SectionHeader = ({ id, title, icon: Icon }) => (
  <div id={id} className="scroll-mt-24">
    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
      <div className="w-12 h-12 bg-linear-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
        <Icon className="text-xl text-white" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 uppercase tracking-wide">{title}</h2>
    </div>
  </div>
)

const SubTermLink = ({ href, children }) => (
  <Link 
    href={href} 
    target="_blank" 
    rel="noopener" 
    className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg hover:bg-orange-50 border border-transparent hover:border-orange-200 transition-all group"
  >
    <BsArrowRight className="text-orange-500" />
    <span className="text-slate-700 group-hover:text-orange-600 transition-colors">{children}</span>
    <FaExternalLinkAlt className="ml-auto text-slate-400 text-xs" />
  </Link>
)

export const Terms = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-linear-to-br from-slate-900 via-slate-800 to-orange-900 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-400/30 rounded-full px-4 py-2 text-orange-300 text-sm font-medium mb-6">
              <FaGavel />
              Legal Document
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              StringsSG Pte Ltd Terms of Service
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <TableOfContents />

        {/* General Terms */}
        <section className="mb-16">
          <SectionHeader id="general-terms" title="General Terms" icon={FaFileContract} />
          
          <div className="prose prose-slate max-w-none space-y-8">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">1. Terms</h3>
              <p className="text-slate-600 leading-relaxed">
                By accessing the website at <a className="text-orange-600 hover:text-orange-700" href="https://www.stringssg.com/">www.stringssg.com</a>,
                you are agreeing to be bound by these terms of service, all
                applicable laws and regulations, and agree that you are responsible
                for compliance with any applicable local laws. If you do not agree
                with any of these terms, you are prohibited from using or accessing
                this site. The materials contained in this website are protected by
                applicable copyright and trademark law.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">2. Use License</h3>
              <ol className="list-disc pl-6 space-y-3 text-slate-600">
                <li>Permission is granted to temporarily download one copy of
                    the materials (information or software) on StringsSG Pte Ltd&apos;s
                    website for personal, non-commercial transitory viewing only. This
                    is the grant of a license, not a transfer of title, and under this
                    license you may not:
                    <ol className="list-decimal pl-6 py-3 space-y-1">
                        <li>modify or copy the materials;</li>
                        <li>use the materials for any commercial purpose, or for any
                            public display (commercial or non-commercial);</li>
                        <li>attempt to decompile or reverse engineer any software
                            contained on StringsSG Pte Ltd&apos;s website;</li>
                        <li>remove any copyright or other proprietary notations from
                            the materials; or</li>
                        <li>transfer the materials to another person or &quot;mirror&quot; the
                            materials on any other server.</li>
                    </ol>
                </li>
                <li>This license shall automatically terminate if you violate
                    any of these restrictions and may be terminated by StringsSG Pte Ltd
                    at any time. Upon terminating your viewing of these materials or
                    upon the termination of this license, you must destroy any
                    downloaded materials in your possession whether in electronic or
                    printed format.</li>
              </ol>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">3. Disclaimer</h3>
              <ol className="list-disc pl-6 space-y-3 text-slate-600">
                <li>The materials on StringsSG Pte Ltd&apos;s website are provided on
                    an &apos;as is&apos; basis. StringsSG Pte Ltd makes no warranties, expressed
                    or implied, and hereby disclaims and negates all other warranties
                    including, without limitation, implied warranties or conditions of
                    merchantability, fitness for a particular purpose, or
                    non-infringement of intellectual property or other violation of
                    rights.</li>
                <li>Further, StringsSG Pte Ltd does not warrant or make any
                    representations concerning the accuracy, likely results, or
                    reliability of the use of the materials on its website or otherwise
                    relating to such materials or on any sites linked to this site.</li>
              </ol>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">4. Limitations</h3>
              <p className="text-slate-600 leading-relaxed">
                In no event shall StringsSG Pte Ltd or its suppliers be liable
                for any damages (including, without limitation, damages for loss of
                data or profit, or due to business interruption) arising out of the
                use or inability to use the materials on StringsSG Pte Ltd&apos;s website,
                even if StringsSG Pte Ltd or a StringsSG Pte Ltd authorized
                representative has been notified orally or in writing of the
                possibility of such damage. Because some jurisdictions do not allow
                limitations on implied warranties, or limitations of liability for
                consequential or incidental damages, these limitations may not apply
                to you.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">5. Accuracy of materials</h3>
              <p className="text-slate-600 leading-relaxed">
                The materials appearing on StringsSG Pte Ltd website could
                include technical, typographical, or photographic errors. StringsSG
                Pte Ltd does not warrant that any of the materials on its website are
                accurate, complete or current. StringsSG Pte Ltd may make changes to
                the materials contained on its website at any time without notice.
                However StringsSG Pte Ltd does not make any commitment to update the
                materials.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">6. Links</h3>
              <p className="text-slate-600 leading-relaxed">
                StringsSG Pte Ltd has not reviewed all of the sites linked to
                its website and is not responsible for the contents of any such
                linked site. The inclusion of any link does not imply endorsement by
                StringsSG Pte Ltd of the site. Use of any such linked website is at
                the user&apos;s own risk.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">7. Communication with other users</h3>
              <p className="text-slate-600 leading-relaxed">
                Communication with other users on StringsSG must be conducted through 
                the features like messaging, call and SMS provided by StringsSG. 
                Effective 28th October 2019 you must not post your mobile number on StringsSG.		     
                Verified phone numbers are directly linked to the Call and SMS features provided by
                StringsSG and must always be used when an interaction requires telephonic 
                conversation.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">8. License to Service Provider&apos;s content</h3>
              <div className="text-slate-600 leading-relaxed space-y-3">
                <p>
                  If you are in present or at any point in time in the past have been a service provider on our platform, give us legal permissions (known as a <q>License</q>)
                  to use your contents from your profile including media. This is solely for the purpose of publicly showcasing your service and improving our products. 
                </p>
                <p>
                  Specifically, when you share, post or upload content that is covered by intellectual property rights on or in connection with our Products, you grant us a non-exclusive, 
                  transferable, sub-licensable, royalty-free and worldwide license to host, use, distribute, modify, run, copy, publicly perform or display, translate and create derivative 
                  works of your content. If you wish, any of your content to not exist on our platform, you can remove them any time by logging in to your account and intimating your intent to 
                  us via email to support@stringssg.com. It may take upto 90 working days for complete removal of your content.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">9. Listings and Selling Products</h3>
              <p className="text-slate-600 leading-relaxed">
                Service providers can list their products on StringsSG platform. However, they must ensure that the listed product and the services are up to date and should be able 
                respond to any enquiries made on their product by an individual or business. The listings should not encourage any illegal transactions. 
                Any listing promoting weapons and illegal substance will be taken down without any prior notice.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">10. Modifications</h3>
              <p className="text-slate-600 leading-relaxed">
                StringsSG Pte Ltd may revise these terms of service for its
                website at any time without notice. By using this website you are
                agreeing to be bound by the then current version of these terms of
                service.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">11. Refund on Service Provider Subscription</h3>
              <p className="text-slate-600 leading-relaxed">
                Service provider subscriptions are non-refundable. However, subscriptions can be cancelled by logging in to you account, which with stop any recurring billing.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">12. Breach of Terms for Service Providers</h3>
              <div className="text-slate-600 leading-relaxed space-y-3">
                <p>
                  If a service request opened by the client has a monetary value associated with it, the client will be making the payment via the StringsSG platform.
                  Charging clients outside of the knowledge of StringsSG is a breach of terms and is not allowed.
                </p>
                <p>
                  In the case, when any additional work for an assigned job is determined as any point in time, the service provider must charge the client with the additional cost
                  through the StringsSG platform.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">13. Governing Law</h3>
              <p className="text-slate-600 leading-relaxed">
                These terms and conditions are governed by and construed in
                accordance with the laws of Singapore and you irrevocably submit to
                the exclusive jurisdiction of the courts in that State or location.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">14. Anti-Discrimination Policy</h3>
              <p className="text-slate-600 leading-relaxed">
                Please refer to our <Link href="/terms/anti-discrimination" className="text-orange-600 hover:text-orange-700 font-medium">Anti-Discrimination Policy</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* Cookie Terms */}
        <section className="mb-16">
          <SectionHeader id="cookie-terms" title="Cookie Usage" icon={FaCookieBite} />
          
          <div className="prose prose-slate max-w-none space-y-6">
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
              <p className="text-slate-700 leading-relaxed">
                These Terms of Service govern the use of cookies on domain stringssg.com (referred to as &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). By using our app or website, 
                you agree to comply with the following cookie usage policy.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">1. Cookie Usage</h3>
              <p className="text-slate-600 leading-relaxed">
                We use cookies on our website to enhance your user experience. Cookies are small text files that are stored on your computer or device when you visit our website. 
                These cookies allow us to improve our website&apos;s functionality, understand user preferences, and provide you with a more personalized and efficient browsing experience.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">2. Types of Cookies</h3>
              <p className="text-slate-600 mb-4">We may use the following types of cookies on our website:</p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex gap-2"><span className="text-orange-500">•</span><span><strong>Essential Cookies:</strong> These cookies are necessary for the basic functionality of our website. They enable you to navigate the website and use its features.</span></li>
                <li className="flex gap-2"><span className="text-orange-500">•</span><span><strong>Analytical/Performance Cookies:</strong> These cookies help us analyze how visitors use our website, so we can improve its performance and user experience.</span></li>
                <li className="flex gap-2"><span className="text-orange-500">•</span><span><strong>Functionality Cookies:</strong> These cookies are used to remember your preferences and settings to provide a more customized browsing experience.</span></li>
                <li className="flex gap-2"><span className="text-orange-500">•</span><span><strong>Third-Party Cookies:</strong> Some cookies may be set by third-party services integrated into our website, such as social media platforms or analytics providers.</span></li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">3. Your Consent</h3>
              <p className="text-slate-600 leading-relaxed">
                By using our website, you consent to the use of cookies as described in this policy. You have the option to disable cookies in your web browser settings, but please note 
                that certain features of our website may not function correctly if cookies are disabled.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">4. Cookies for Analytics</h3>
              <p className="text-slate-600 leading-relaxed">
                We use analytical cookies to collect information about how you use our website, such as which pages you visit and how long you spend on our site. This information helps us 
                improve the performance and content of our website.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">5. Personal Information</h3>
              <p className="text-slate-600 leading-relaxed">
                Cookies do not typically contain personally identifiable information. However, we may link the information stored in cookies to any personal information you provide to us. 
                We treat this combined information in accordance with our Privacy Policy.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">6. Third-Party Cookies</h3>
              <p className="text-slate-600 leading-relaxed">
                Some content or features on our website may be provided by third-party services, and they may set their own cookies. We have no control over the cookies used by these 
                third-party services. Please refer to their respective privacy and cookie policies for more information.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">7. Changing Cookie Settings</h3>
              <p className="text-slate-600 leading-relaxed">
                You can modify your cookie preferences by adjusting the settings in your web browser. Refer to your browser&apos;s help documentation for instructions on how to manage cookies.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-3">8. Changes to Cookie Policy</h3>
              <p className="text-slate-600 leading-relaxed">
                We may update or modify this cookie usage policy at any time without prior notice. It is your responsibility to review the policy periodically to stay informed about any changes.
              </p>
            </div>

            <div className="bg-slate-100 rounded-xl p-6">
              <p className="text-slate-700 leading-relaxed">
                By using our app or website, you agree to abide by this cookie usage policy. If you do not agree with these terms, please refrain from using our website.
              </p>
            </div>
          </div>
        </section>

        {/* Consumer Client Terms */}
        <section className="mb-16">
          <SectionHeader id="terms-for-consumer-clients" title="Terms for Consumer Clients" icon={FaUsers} />
          
          <div className="prose prose-slate max-w-none space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <p className="text-slate-700 leading-relaxed">
                You are a consumer client for StringsSG, if you have registered an account with us and have availed any professional service like handyman service, 
                air conditioner servicing, personal training, domestic helper etc.
              </p>
            </div>

            <p className="text-slate-600">
              When using StringsSG services, you agree to the below terms. Click on each of them to read carefully:
            </p>

            <div className="space-y-3">
              <SubTermLink href="/terms/limitation-of-liability-for-direct-user-service-provider-transaction">
                Limitation of Liability for Direct User – Service Provider Transactions
              </SubTermLink>
              <SubTermLink href="/terms/consumer-clients-payment-for-service">
                Terms On Consumer Clients Payment For Service
              </SubTermLink>
              <SubTermLink href="/terms/refund-policy-on-service-for-consumer-client">
                Refund Policy On Service For Consumer Clients
              </SubTermLink>
              <SubTermLink href="/terms/consumer-warranty">
                Consumer Service Warranty
              </SubTermLink>
              <SubTermLink href="/terms/personal-trainer-service-terms-for-clients">
                Personal Trainer Service Terms For Clients
              </SubTermLink>
            </div>
          </div>
        </section>

        {/* Service Provider Terms */}
        <section className="mb-16">
          <SectionHeader id="terms-for-service-providers" title="Terms for Service Providers" icon={FaUserTie} />
          
          <div className="prose prose-slate max-w-none space-y-6">
            <p className="text-slate-600">
              If you are a service provider on the StringsSG platform you agree to the below terms. Click on each of them to read carefully:
            </p>

            <div className="space-y-3">
              <SubTermLink href="/terms/obligations">
                Obligations
              </SubTermLink>
              <SubTermLink href="/terms/packaged-service-for-service-provider">
                Packaged Service For Service Provider
              </SubTermLink>
              <SubTermLink href="/terms/payment-to-service-provider">
                Payment To Service Provider
              </SubTermLink>
              <SubTermLink href="/terms/cancellation-of-request">
                Cancellation Of Request
              </SubTermLink>
              <SubTermLink href="/terms/warranty-claims">
                Warranty Claims By Service Requester
              </SubTermLink>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
